import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

const CHANNEL_LABELS = { walk_in: "Walk-in", phone: "Phone", website: "Website", easystay_app: "EasyStay App" };

export default function RevenueTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const { fmt } = useCurrency();

  useEffect(() => {
    setData(null);
    api.get(`/accounting/revenue?days=${days}`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  }, [days]);

  if (!data)
    return <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  const channels = Object.entries(data.booked_by_channel);
  const max = Math.max(1, ...channels.map(([, v]) => v));
  const live = data.live_pull || {};

  return (
    <div className="space-y-4" data-testid="revenue-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Booking revenue flows in automatically from bookings and EasyStay — no manual entry.
        </p>
        <div className="flex gap-1.5">
          {[7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} data-testid={`revenue-days-${d}`}
              onClick={() => setDays(d)} className="transition-colors">
              {d}d
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="border border-border bg-card p-5" data-testid="revenue-booked-card">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Booked revenue ({days}d)</div>
          <div className="font-heading mt-2 text-3xl font-bold" data-testid="revenue-booked-value">{fmt(data.booked_total)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{data.bookings.length} bookings in window</div>
        </div>
        <div className="border border-border bg-card p-5" data-testid="revenue-collected-card">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Collected ({days}d)</div>
          <div className="font-heading mt-2 text-3xl font-bold text-status-confirmed" data-testid="revenue-collected-value">{fmt(data.collected_total)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{data.collected_count} payments received</div>
        </div>
        <div className="border border-border bg-card p-5" data-testid="revenue-easystay-card">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">From EasyStay platform</div>
          <div className="font-heading mt-2 text-3xl font-bold" data-testid="revenue-easystay-value">{fmt(data.easystay_booked)}</div>
          <div className="mt-1 text-xs text-muted-foreground">{data.easystay_count} synced bookings</div>
        </div>
        <div className={`border p-5 ${live.status === "ok" ? "border-status-confirmed/40 bg-status-confirmed/5" : "border-border bg-card"}`} data-testid="revenue-live-pull-card">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">EasyStay live pull</div>
          {live.status === "ok" ? (
            <div className="mt-2 flex items-center gap-2 text-sm font-medium text-status-confirmed">
              <CheckCircle size={18} weight="fill" /> Live — reading from easystayzm.com
            </div>
          ) : (
            <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
              <Warning size={16} className="mt-0.5 shrink-0 text-status-pending" />
              <span data-testid="live-pull-pending">{live.detail || "Not configured"}</span>
            </div>
          )}
          <div className="mt-2 text-[11px] text-muted-foreground">GET easystayzm.com/api/sync/manager/revenue · X-Sync-Key</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="border border-border bg-card p-5" data-testid="revenue-by-channel">
          <h2 className="font-heading text-lg font-bold">Booked revenue by channel</h2>
          <div className="mt-4 space-y-3">
            {channels.length === 0 && <div className="text-sm text-muted-foreground">No bookings in this window.</div>}
            {channels.map(([ch, val]) => (
              <div key={ch} data-testid={`channel-bar-${ch}`}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{CHANNEL_LABELS[ch] || ch}</span>
                  <span className="font-heading font-bold">{fmt(val)}</span>
                </div>
                <div className="mt-1 h-2 w-full rounded-full bg-secondary">
                  <div className="h-2 rounded-full bg-primary transition-[width] duration-500" style={{ width: `${(val / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border bg-card xl:col-span-2" data-testid="revenue-bookings-table">
          <div className="border-b border-border p-5">
            <h2 className="font-heading text-lg font-bold">Recent revenue bookings</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 font-medium">Guest</th>
                <th className="p-3 font-medium">Channel</th>
                <th className="p-3 font-medium">Dates</th>
                <th className="p-3 text-right font-medium">Total</th>
                <th className="p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.bookings.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0" data-testid={`revenue-booking-${b.id}`}>
                  <td className="p-3 font-medium">{b.guest_name}</td>
                  <td className="p-3 text-xs">{CHANNEL_LABELS[b.channel] || "Walk-in"}</td>
                  <td className="p-3 text-muted-foreground">{b.check_in} → {b.check_out}</td>
                  <td className="p-3 text-right font-medium">{fmt(b.total)}</td>
                  <td className="p-3"><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
